'use client';
import { useEffect, useRef, useState } from 'react';

/**
 * 카드 썸네일 호버 미리보기 (2026-08-22 사용자 확정 동작).
 *
 * **잠깐 머물러야 열린다**(0.6초, `OPEN_DELAY_MS` — 2026-08-22 사용자 확정). 목록을 훑는 동안 마우스가 스쳐
 * 가는 카드마다 영상이 뜨면 화면이 정신없고 CDN 도 헛돈다. 열릴 때는 썸네일 자리에
 * **16:9 로 살짝 커지며** 뜬다 — 썸네일은 4:3 이라, **높이를 기준으로** 늘려야
 * 이미지 영역이 좌우로 펼쳐지는 모양이 된다(폭 기준으로 잡으면 세로가 되레 줄어든다).
 *
 * **한 바퀴만 돌고 스스로 닫는다**(2026-08-22 사용자 지시 "계속 반복되게 하지 말고").
 * 마우스를 올려 둔 채 두면 영상이 끝없이 반복돼 시선을 붙잡는다. 끝나면 원래 썸네일로
 * 돌아가고, 다시 보려면 마우스를 뺐다가 다시 올린다.
 *
 * 닫힐 때는 **열릴 때의 반대로 접힌다**(사용자 지시) — 펼쳐질 때처럼 가로로만 움직여
 * 썸네일 크기로 돌아가며 사라진다. 그래서 닫는 것도 한 박자 뒤에 실제로 치운다.
 *
 * 두 갈래가 같은 자리에서 나온다 — 서버가 `kind` 로 갈라 준다.
 *  - `intro`      : OVP 인트로 클립. **진짜 영상**이라 확대가 없어 선명하다
 *  - `storyboard` : 인트로가 없을 때. 스프라이트 시트를 넘겨 흐르는 인상만 준다
 *
 * 어느 차시를 쓰나 — **맛보기 0번째**(`previews[0]`, 맛보기 정렬 순). 대표를 바꾸려면
 * 관리자에서 맛보기 정렬을 조정한다(대표 지정 칸을 따로 두지 않는다).
 */

/** 서버 응답(`GET /products/curriculums/:ccid/preview`) */
type Preview =
  | { kind: 'intro'; url: string; poster: string | null }
  | {
      kind: 'storyboard';
      sheet: string; tile: [number, number]; grid: [number, number];
      frames: number; interval: number; total: number;
    };

/**
 * 스크럽 설정 — /ux-lab 13번에서 눈으로 맞춰 확정(2026-08-22 사용자).
 *
 * **시트의 칸을 다 쓴다**(프레임 수를 줄이지 않는다) — 칸을 건너뛰면 장면이 튄다.
 * 한 바퀴 4.4초. **겹쳐 넘기기(크로스페이드)는 쓰지 않는다** — 겹치면 두 장면이 섞여
 * 흐릿해 보인다. 칸을 다 쓰면 간격이 촘촘해 겹치지 않아도 튀지 않는다.
 */
const SCRUB_LOOP_MS = 4400;
/**
 * 여는 데 필요한 머무름 — 스쳐 가는 카드에는 열리지 않는다.
 *
 * **0.6초로 확정**(2026-08-22 사용자). /ux-lab 조절기로 0~1.5초를 재 보고 정했다.
 * 참고한 값 — 넷플릭스 0.4초·유튜브 0.6초 안팎으로 알려져 있으나 공개된 수치는 아니다.
 * 우리 조건은 그들과 다르다는 점도 함께 봤다 — 큰 타일 몇 개가 아니라 작은 카드 20장
 * 격자라 스치는 카드가 많고, 미리 물고 있는 게 아니라 그때 500KB 를 받는다.
 * 그래도 0.35초에 주소를 미리 받아 두므로 여는 순간의 지연은 영상 로딩뿐이다.
 */
const OPEN_DELAY_MS = 600;
/** 주소는 먼저 받아 둔다 — 여는 순간 영상이 바로 붙게(작은 JSON 이라 부담이 없다) */
const FETCH_DELAY_MS = 350;
/** 접히는 데 걸리는 시간 — CSS `cp-out` 과 **같은 값이어야** 중간에 툭 끊기지 않는다 */
const CLOSE_MS = 180;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 썸네일에 붙이는 호버 미리보기.
 * @param ccid 맛보기 0번째 차시. 없으면(`undefined`) 아무 일도 하지 않는다
 * @param openDelay 여는 데 필요한 머무름(ms). /ux-lab 이 값을 바꿔 가며 재는 데 쓴다
 * @returns `bind` 를 썸네일 `<div>` 에 펴고, `node` 를 그 안에 그린다
 */
/** 미리보기를 막는 자리인가 — 썸네일 위에 얹힌 조작 버튼(찜 하트)이 이 표시를 단다 */
const inBlocked = (n: EventTarget | null) =>
  n instanceof Element && n.closest('[data-no-preview]') != null;

export function useHoverPreview(ccid?: number, openDelay = OPEN_DELAY_MS) {
  /** 닫힘 → 열림 → **접히는 중** → 닫힘. 접히는 동안에도 그려 둬야 연출이 보인다 */
  const [phase, setPhase] = useState<'closed' | 'open' | 'closing'>('closed');
  const [pv, setPv] = useState<Preview | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => clear, []);

  // 차시가 바뀌면 받아 둔 것을 버린다(같은 카드가 다른 상품을 그릴 수 있다)
  useEffect(() => { setPv(null); setPhase('closed'); }, [ccid]);

  if (!ccid) return { bind: {}, node: null };

  const enter = () => {
    clear();
    timers.current.push(setTimeout(() => {
      if (pv) return;
      fetch(`/api/products/curriculums/${ccid}/preview`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d: Preview | null) => { if (d) setPv(d); })
        .catch(() => { /* CDN·서버가 죽어도 카드는 그대로 동작해야 한다 */ });
    }, FETCH_DELAY_MS));
    timers.current.push(setTimeout(() => setPhase('open'), Math.max(0, openDelay)));
  };

  /**
   * 접으면서 닫는다 — 다 접힌 뒤에 치운다. 움직임을 줄인 사용자에게는 바로 치운다
   * (연출이 없으니 기다릴 이유도 없다).
   */
  const close = () => {
    clear();
    /*
     * 판단을 **업데이터 밖에서** 한다. 예전에는 `setPhase((p) => …)` 안에서 타이머를
     * 걸었는데, 업데이터는 순수해야 한다 — StrictMode 는 두 번 부르므로 타이머도 두 번
     * 걸린다(같은 이유로 `Scrub` 에서 경고가 났다). 지금 값은 클로저로 충분하다.
     */
    if (phase !== 'open' || reducedMotion()) { setPhase('closed'); return; }
    setPhase('closing');
    timers.current.push(setTimeout(() => setPhase('closed'), CLOSE_MS));
  };

  return {
    /*
     * 썸네일 위에 얹힌 **조작 버튼(찜 하트) 위에서는 재생하지 않는다**(2026-08-23 사용자 지시).
     * 그 버튼도 썸네일 안이라 마우스를 올리면 미리보기가 열려 하트를 가린다.
     *
     * `mouseenter` 는 안쪽으로 옮겨 다닐 때 다시 오지 않으므로 `mouseover`/`mouseout`
     * (버블링하는 짝)으로 안쪽 이동을 본다 — 버튼에 들어가면 닫고, 버튼에서 썸네일로
     * 돌아오면 다시 무장한다. 막을 자리는 `data-no-preview` 로 표시한다.
     */
    bind: {
      onMouseEnter: (e: React.MouseEvent) => { if (!inBlocked(e.target)) enter(); },
      onMouseLeave: close,
      onMouseOver: (e: React.MouseEvent) => { if (inBlocked(e.target)) close(); },
      onMouseOut: (e: React.MouseEvent) => {
        const back = e.relatedTarget;
        if (inBlocked(e.target) && !inBlocked(back) && back instanceof Node && e.currentTarget.contains(back)) enter();
      },
    },
    /*
     * `done` 이 오면 닫는다. 마우스는 아직 위에 있지만 다시 열지 않는다 —
     * 여는 타이머는 `enter` 에서만 걸리므로, 뺐다가 다시 올려야 한 번 더 본다.
     */
    node: phase !== 'closed' && pv
      ? <PreviewBox pv={pv} closing={phase === 'closing'} onDone={close} />
      : null,
  };
}

/** 열린 미리보기 상자 — 썸네일 가운데에 16:9 로 살짝 커지며 뜬다 */
function PreviewBox({ pv, closing, onDone }: { pv: Preview; closing: boolean; onDone: () => void }) {
  return (
    <div className={`cp-wrap${reducedMotion() ? ' no-anim' : ''}${closing ? ' is-closing' : ''}`}>
      {pv.kind === 'intro'
        ? (
          /*
           * muted 라야 브라우저가 자동재생을 허용한다(정책). poster 는 첫 프레임이 오기 전의
           * 빈 검정을 막고, preload='none' 이라 **열리기 전에는 아무것도 받지 않는다**.
           * `loop` 를 쓰지 않는다 — 한 번 끝나면(`onEnded`) 스스로 닫는다.
           * 못 트는 경우(자동재생 차단 등)도 닫아 준다 — 포스터만 붙잡고 있으면 안 된다.
           */
          <video className="cp-media" src={pv.url} poster={pv.poster ?? undefined}
            autoPlay muted playsInline preload="none" onEnded={onDone} onError={onDone} />
        )
        : <Scrub sb={pv} onDone={onDone} />}
    </div>
  );
}

/**
 * 스프라이트 스크럽 — 두 겹을 겹쳐 두고 번갈아 보여 준다(크로스페이드).
 * 칸이 2초 간격이라 딱 끊어 바꾸면 슬라이드쇼로 보인다. 붙어 있는 칸을 순서대로 넘겨
 * **같은 장면이 이어지게** 한다(전체에 흩어 뽑으면 장면이 통째로 바뀐다).
 */
function Scrub({ sb, onDone }: { sb: Extract<Preview, { kind: 'storyboard' }>; onDone: () => void }) {
  const [frame, setFrame] = useState(0);
  const n = sb.frames; // 시트에 든 칸을 다 쓴다
  const last = Math.max(0, n - 1);
  /*
   * `onDone` 은 렌더마다 새 함수다 — 그대로 의존성에 넣으면 타이머가 매번 다시 걸려
   * 스크럽이 첫 칸에서 맴돈다(검색 팔레트에서 겪은 것과 같은 부류다). 최신 것만 들고 있는다.
   */
  const done = useRef(onDone);
  useEffect(() => { done.current = onDone; });

  /*
   * 한 칸씩 넘긴다 — `setInterval` 대신 **칸마다 새 타이머**를 건다. 마지막 칸에 닿으면
   * 스스로 서므로 멈추라고 따로 이를 필요가 없다.
   */
  useEffect(() => {
    if (reducedMotion() || n < 2 || frame >= last) return; // 움직임을 줄였으면 첫 칸만
    const id = setTimeout(() => setFrame((f) => f + 1), Math.max(40, SCRUB_LOOP_MS / n));
    return () => clearTimeout(id);
  }, [frame, last, n]);

  /*
   * 다 돌면 닫는다 — **이펙트에서** 부른다. 예전에는 `setFrame` 의 업데이터 안에서
   * 불렀는데, 업데이터는 **렌더 중에** 실행되므로 그 안에서 부모 상태를 바꾸면
   * "Cannot update a component while rendering a different component" 가 뜬다
   * (2026-08-22 사용자 제보). 업데이터에는 부작용을 넣지 않는다.
   */
  useEffect(() => {
    if (reducedMotion() || n < 2) return; // 첫 칸만 보여 주는 경우는 스스로 닫지 않는다
    if (frame >= last) done.current();
  }, [frame, last, n]);

  return <span className="cp-media" style={cell(sb, Math.min(frame, last))} />;
}

/**
 * 시트에서 한 칸을 잘라 보여 주는 배경 좌표.
 * 퍼센트 배경 위치는 **"남는 공간의 비율"** 이라 `col/(cols-1)*100%` 여야 한다 —
 * 픽셀 오프셋으로 계산하면 한 칸만 보이고 멈춘다(2026-08-22 실측으로 잡은 버그).
 */
function cell(sb: Extract<Preview, { kind: 'storyboard' }>, idx: number): React.CSSProperties {
  const [cols, rows] = sb.grid;
  const col = idx % cols;
  const row = Math.floor(idx / cols);
  return {
    backgroundImage: `url(${sb.sheet})`,
    backgroundSize: `${cols * 100}% ${rows * 100}%`,
    backgroundPosition: `${cols > 1 ? (col / (cols - 1)) * 100 : 0}% ${rows > 1 ? (row / (rows - 1)) * 100 : 0}%`,
  };
}
